import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { unifiedGetDoc } from '@watchlist/shared';
import InitialsAvatar from '../widgets/InitialsAvatar';
import BottomSheet, { BottomSheetDraggableHeader } from './BottomSheet';
import { font } from '../../utils/fontHelper';
import { glassStyles, getColors } from '../../tokens/designTokens';

const PAGE_SIZE = 50;

interface LikedByUser {
  uid: string;
  displayName: string;
  username?: string;
  photoURL?: string;
}

interface LikedByBottomSheetProps {
  open: boolean;
  onClose: () => void;
  userIds: string[];
  title?: string;
  onUserClick?: (uid: string) => void;
  /** Override zIndex (use when rendered inside a high-zIndex overlay). */
  zIndex?: number;
}

const LikedByBottomSheet: React.FC<LikedByBottomSheetProps> = ({
  open,
  onClose,
  userIds,
  title = 'Liked by',
  onUserClick,
  zIndex,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const colors = getColors(isDark);

  const [users, setUsers] = useState<LikedByUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const cacheRef = useRef<Map<string, LikedByUser>>(new Map());

  useEffect(() => {
    if (!open || userIds.length === 0) {
      setUsers([]);
      setVisibleCount(PAGE_SIZE);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const fetchUsers = async () => {
      const result: LikedByUser[] = [];
      const toFetch = userIds.filter(uid => !cacheRef.current.has(uid));

      await Promise.all(
        toFetch.map(async (uid) => {
          try {
            const snap = await unifiedGetDoc('userProfiles', uid);
            const data = snap?.data ? snap.data() : snap;
            if (data) {
              const user: LikedByUser = {
                uid,
                displayName: data.displayName || 'Unknown',
                username: data.username,
                photoURL: data.photoURL,
              };
              cacheRef.current.set(uid, user);
            }
          } catch {
            // Skip users that fail to load
          }
        })
      );

      if (cancelled) return;

      for (const uid of userIds) {
        const cached = cacheRef.current.get(uid);
        if (cached) result.push(cached);
      }

      setUsers(result);
      setLoading(false);
    };

    fetchUsers();
    return () => { cancelled = true; };
  }, [open, userIds]);

  const visibleUsers = users.slice(0, visibleCount);
  const hasMore = visibleCount < users.length;

  const content = (
    <>
      <BottomSheetDraggableHeader sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 3,
        pt: 3,
        pb: 2,
        flexShrink: 0,
      }}>
        <Typography sx={{
          fontFamily: font(),
          fontWeight: 600,
          fontSize: 18,
          color: colors.text.primary,
        }}>
          {title}
          {users.length > 0 && (
            <Typography component="span" sx={{
              fontFamily: font(),
              fontWeight: 400,
              fontSize: 14,
              color: colors.text.secondary,
              ml: 1,
            }}>
              {users.length}
            </Typography>
          )}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: colors.text.secondary, '&:hover': { bgcolor: colors.ui.hover } }}>
          <Close />
        </IconButton>
      </BottomSheetDraggableHeader>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 1, pb: isMobile ? 'env(safe-area-inset-bottom, 16px)' : 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : users.length === 0 ? (
          <Typography sx={{
            textAlign: 'center',
            color: colors.text.secondary,
            fontFamily: font(),
            fontSize: 14,
            py: 4,
          }}>
            No likes yet
          </Typography>
        ) : (
          <List disablePadding>
            {visibleUsers.map((user) => (
              <ListItem key={user.uid} disablePadding>
                <ButtonBase
                  onClick={() => onUserClick?.(user.uid)}
                  disabled={!onUserClick}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    px: 2,
                    py: 1.25,
                    borderRadius: 2,
                    textAlign: 'left',
                    '&:hover': onUserClick ? { bgcolor: colors.ui.hover } : undefined,
                  }}
                >
                  <InitialsAvatar
                    src={user.photoURL}
                    name={user.displayName}
                    size={40}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{
                      fontFamily: font(),
                      fontWeight: 600,
                      fontSize: 14,
                      color: colors.text.primary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {user.displayName}
                    </Typography>
                    {user.username && (
                      <Typography sx={{
                        fontFamily: font(),
                        fontSize: 13,
                        color: colors.text.secondary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        @{user.username}
                      </Typography>
                    )}
                  </Box>
                </ButtonBase>
              </ListItem>
            ))}

            {hasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                <ButtonBase
                  onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
                  sx={{
                    px: 3,
                    py: 1,
                    borderRadius: 2,
                    color: colors.text.secondary,
                    fontFamily: font(),
                    fontSize: 14,
                    '&:hover': { bgcolor: colors.ui.hover },
                  }}
                >
                  Show more
                </ButtonBase>
              </Box>
            )}
          </List>
        )}
      </Box>
    </>
  );

  if (isMobile) {
    return (
      <BottomSheet
        open={open}
        onClose={onClose}
        maxHeight="70vh"
        zIndex={zIndex}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.5, flexShrink: 0 }}>
          <Box sx={{
            width: 48,
            height: 4,
            borderRadius: 999,
            bgcolor: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)',
          }} />
        </Box>
        {content}
      </BottomSheet>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '70vh',
          display: 'flex',
          flexDirection: 'column',
          ...glassStyles.popup(isDark),
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>{/* handled inline */}</DialogTitle>
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {content}
      </DialogContent>
    </Dialog>
  );
};

export default LikedByBottomSheet;
